import { FC, useCallback, useEffect, useState } from 'react';
import { noOp } from '../../lib/utils';

export type Tab = {
  key: string;
  name: string;
  content: string | JSX.Element;
};
export type TabGroupProps = {
  tabs: Tab[];
  initialActiveIndex?: number;
  onTabSwitch?: (tab: Tab) => void;
};

export const TabGroup: FC<TabGroupProps> = ({
  tabs,
  initialActiveIndex = 0,
  onTabSwitch = noOp,
}) => {
  const [activeIndex, setActiveIndex] = useState(
    tabs[initialActiveIndex] ? initialActiveIndex : 0,
  );

  useEffect(() => {
    if (initialActiveIndex === undefined || initialActiveIndex < 0) return;
    setActiveIndex(tabs[initialActiveIndex] ? initialActiveIndex : 0);
  }, [tabs, initialActiveIndex]);

  const changeTab = useCallback(
    (nextTabIndex: number) => {
      if (nextTabIndex === activeIndex) return;

      const nextTab = tabs[nextTabIndex];

      if (!nextTab) return;

      setActiveIndex(nextTabIndex);
      onTabSwitch(nextTab);
    },
    [activeIndex, onTabSwitch, tabs],
  );

  return (
    <>
      <div className="tabgroup-tabs" data-testid="tabgroup">
        <div className="overflow-x-auto border-b border-gray-500">
          <nav className="flex" aria-label="Tabs">
            {tabs.map(({ name: tabName }, index) => (
              <button
                key={tabName}
                className={`
                    ${
                      index === activeIndex
                        ? 'border-blue-100 text-blue-100 __active__'
                        : 'border-transparent text-gray-700 hover:text-blue-100'
                    } flex-shrink-0 whitespace-nowrap p-2 border-b-4 font-medium text-sm uppercase focus:outline-none`}
                aria-current={index === activeIndex ? 'page' : undefined}
                data-testid='tabgroup-tab'
                onClick={(event) => {
                  event.preventDefault();
                  changeTab(index);
                }}
              >
                {tabName}
              </button>
            ))}
          </nav>
        </div>
      </div>
      <div className="tabgroup-content">{tabs[activeIndex].content}</div>
    </>
  );
};
